import React, { useState } from 'react';
import { useCart } from '../../CartContext';
import { Link } from 'react-router-dom';
import './Cart.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const Cart = () => {
  const { cartItems, cartCount, removeFromCart, updateQuantity, clearCart } = useCart();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Le nom complet est requis';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email invalide';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Le téléphone est requis';
    } else if (!/^[0-9]{8,}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Numéro de téléphone invalide';
    }
    
    if (!formData.address.trim()) {
      errors.address = 'L\'adresse est requise';
    }
    
    if (!formData.city.trim()) {
      errors.city = 'La ville est requise';
    }
    
    if (!formData.postalCode.trim()) {
      errors.postalCode = 'Le code postal est requis';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  /* eslint-disable no-unused-vars */
  const handleCheckoutClick = () => {
    setShowCheckoutModal(true);
  };

  const handleCloseModal = () => {
    setShowCheckoutModal(false);
    setFormErrors({});
  };

  const handleConfirmOrder = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      console.log('Commande confirmée:', {
        items: cartItems,
        customerInfo: formData,
        total: getTotalPrice()
      });
      
      alert('Votre commande a été confirmée avec succès!');
      
      clearCart();
      setShowCheckoutModal(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        notes: ''
      });
    }
  };

  if (cartCount === 0) {
    return (
      <div className="cart-container">
        <div className="cart-empty">
          <i className="fas fa-shopping-cart"></i>
          <h2>Votre panier est vide</h2>
          <p>Ajoutez des produits pour commencer vos achats</p>
          <Link to="/products" className="btn-continue-shopping">
            <i className="fas fa-arrow-left"></i>
            Continuer mes achats
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-wrapper">
        <div className="cart-header">
          <h1>
            <i className="fas fa-shopping-cart"></i>
            Mon Panier
          </h1>
          <span className="cart-count-badge">{cartCount} article{cartCount > 1 ? 's' : ''}</span>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <div className="item-image">
                  <img src={item.adresse_Image_Product} alt={item.name} />
                </div>
                
                <div className="item-details">
                  <h3>{item.name}</h3>
                  {item.reference && (
                    <p className="item-reference">Réf: {item.reference}</p>
                  )}
                  {item.brand && (
                    <p className="item-brand">Marque: {item.brand}</p>
                  )}
                </div>

                <div className="item-quantity">
                  <button 
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                    <i className="fas fa-plus"></i>
                  </button>
                </div>

                <div className="item-price">
                  <span className="unit-price">{item.price.toLocaleString('fr-TN')} DT</span>
                  <span className="total-price">
                    {(item.price * item.quantity).toLocaleString('fr-TN')} DT
                  </span>
                </div>

                <button 
                  className="btn-remove"
                  onClick={() => removeFromCart(item.id)}
                  title="Supprimer"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Résumé de la commande</h2>
            
            <div className="summary-row">
              <span>Sous-total ({cartCount} article{cartCount > 1 ? 's' : ''})</span>
              <span>{getTotalPrice().toLocaleString('fr-TN')} DT</span>
            </div>

            <div className="summary-row">
              <span>Livraison</span>
              <span className="free-shipping">Gratuite</span>
            </div>

            <div className="summary-divider"></div>

            <div className="summary-row total">
              <span>Total TTC</span>
              <span className="total-amount">{getTotalPrice().toLocaleString('fr-TN')} DT</span>
            </div>

            <Link to="/checkout" className="btn-checkout">
              <i className="fas fa-credit-card"></i>
              Passer la commande
            </Link>             

            <button className="btn-clear-cart" onClick={clearCart}>
              <i className="fas fa-trash"></i>
              Vider le panier
            </button>

            <Link to="/products" className="btn-continue">
              <i className="fas fa-arrow-left"></i>
              Continuer mes achats
            </Link>
          </div>
        </div>
      </div>

      {showCheckoutModal && (
        <div className="checkout-modal-overlay" onClick={handleCloseModal}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-clipboard-check"></i>
                Confirmer votre commande
              </h2>
              <button className="btn-close-modal" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleConfirmOrder}>
                <div className="form-section">
                  <h3>
                    <i className="fas fa-user"></i>
                    Informations personnelles
                  </h3>
                  
                  <div className="form-group">
                    <label htmlFor="fullName">
                      Nom complet <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Entrez votre nom complet"
                      className={formErrors.fullName ? 'error' : ''}
                    />
                    {formErrors.fullName && (
                      <span className="error-message">{formErrors.fullName}</span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">
                        Email <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="exemple@email.com"
                        className={formErrors.email ? 'error' : ''}
                      />
                      {formErrors.email && (
                        <span className="error-message">{formErrors.email}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">
                        Téléphone <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="12 345 678"
                        className={formErrors.phone ? 'error' : ''}
                      />
                      {formErrors.phone && (
                        <span className="error-message">{formErrors.phone}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>
                    <i className="fas fa-map-marker-alt"></i>
                    Adresse de livraison
                  </h3>

                  <div className="form-group">
                    <label htmlFor="address">
                      Adresse <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Rue, numéro, appartement..."
                      className={formErrors.address ? 'error' : ''}
                    />
                    {formErrors.address && (
                      <span className="error-message">{formErrors.address}</span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">
                        Ville <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Tunis"
                        className={formErrors.city ? 'error' : ''}
                      />
                      {formErrors.city && (
                        <span className="error-message">{formErrors.city}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="postalCode">
                        Code postal <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="1000"
                        className={formErrors.postalCode ? 'error' : ''}
                      />
                      {formErrors.postalCode && (
                        <span className="error-message">{formErrors.postalCode}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">
                      Notes de livraison (optionnel)
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Instructions spéciales pour la livraison..."
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                <div className="order-summary-modal">
                  <h3>
                    <i className="fas fa-receipt"></i>
                    Récapitulatif
                  </h3>
                  <div className="summary-item">
                    <span>Sous-total ({cartCount} article{cartCount > 1 ? 's' : ''})</span>
                    <span>{getTotalPrice().toLocaleString('fr-TN')} DT</span>
                  </div>
                  <div className="summary-item">
                    <span>Livraison</span>
                    <span className="free-shipping">Gratuite</span>
                  </div>
                  <div className="summary-divider"></div>
                  <div className="summary-item total">
                    <span>Total à payer</span>
                    <span className="total-amount">{getTotalPrice().toLocaleString('fr-TN')} DT</span>
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                    <i className="fas fa-times"></i>
                    Annuler
                  </button>
                  <button type="submit" className="btn-confirm">
                    <i className="fas fa-check-circle"></i>
                    Confirmer la commande
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;